import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ClerkProvider, SignedIn, SignedOut, SignIn, useClerk } from '@clerk/clerk-react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { WorkzoneMap } from './components/WorkzoneMap';
import { WorkzoneMapMapbox } from './components/WorkzoneMapMapbox';
import {
  Alert,
  Toolbar, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Typography,
  AppBar,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  TextField,
} from '@mui/material';
import {
  generateAgentPlan,
  getCombinedAnalysis,
  getSignLibrary,
  exportPlan,
} from "./services/api";
import { generateRoadTool, RoadToolGeometry } from "./services/roadTool";
import { resolveApiUrl } from './utils/api';
import { TitleBoxData } from './components/TitleBoxDialog';
import { SubscriptionDialog } from './components/SubscriptionDialog';
import { TrafficSpecialistChat } from './components/TrafficSpecialistChat';
import { DraggableSignLibrary } from './components/DraggableSignLibrary';
import BottomLeftControls from './components/DraggableBottomNav';
import { BottomRightTools, ToolMode } from './components/BottomRightTools';
import { useAuth } from './hooks/useAuth';
import type { ArrowDescriptor, ArrowDraft, ArrowInput } from './types/arrows';
import type { DelineatorLine, DelineatorDraft, DelineatorInput } from './types/delineators';
import { DEFAULT_DELINEATOR_SPACING, DEFAULT_DELINEATOR_SIZE, DEFAULT_DELINEATOR_DEVICE, DEFAULT_DELINEATOR_GEOMETRY } from './types/delineators';
import type { SelectedElement } from './types/selection';
import type { SignEdgeMetadata, PlacedSign } from './types/signs';
import type { SignLine } from './types/signLines';
import type { PageAnnotation } from './types/annotations';
import { AnnotationEditor } from './components/AnnotationEditor';
import { detectSignLines, haversineDistance } from './utils/signLineDetection';
import { syncAutoArrows, updateArrowsForMovedSign, removeArrowsForDeletedSign } from './utils/autoArrows';
import { CompositeSignBuilder, CompositeSign } from './components/CompositeSignBuilder';
import { Generate } from 'components/Generate';
import { PrintSettings, PaperSize, Orientation, getPaperDimensions } from './components/PrintSettings';
import { ProjectSelector } from './components/ProjectSelector';
import { FileMenu } from './components/FileMenu';
import { useProject } from './hooks/useProject';
import type { ProjectData, StoredSign, StoredWorkzone, StoredMapState, WorksiteLocation } from './types/project';
import { DEFAULT_PROJECT_DATA } from './types/project';

const generateId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random()}`;
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#c62828',
    },
    secondary: {
      main: '#ff9100',
    },
  },
  typography: {
    fontFamily: "'Mona-Sans', sans-serif",
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
  },
});



const drawerWidth = 400; // Only used for sign library sidebar

// Removed SimpleAuthHeader - auth is now in main header

const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// Custom Half-Page Sign-In Screen
const SignInScreen: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      flexDirection: { xs: 'column', md: 'row' }
    }}
  >
    {/* Left Side - Branding/Image (hidden on mobile) */}
    <Box
      sx={{
        flex: 1,
        minHeight: { xs: '40vh', md: '100vh' },
        backgroundColor: 'transparent',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        p: { xs: 3, md: 4 },
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.1
        }
      }}
    >
      {/* Background Image - You can replace this URL with your actual image */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url("/images/background.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: -1
        }}
      />
      
      {/* Overlay content (optional - remove if you want just the image) */}
      <Box sx={{ 
        position: 'relative', 
        zIndex: 1,
        textAlign: 'center',
        // Remove this Box completely if you want just the image without any overlay text
        display: 'none' // Hidden by default - you can remove this line if you want overlay text
      }}>
        <Typography 
          variant="h1" 
          sx={{ 
            fontWeight: 700, 
            mb: 3,
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}
        >
          Traffic Designer
        </Typography>
      </Box>
    </Box>

    {/* Right Side - Sign In Form */}
    <Box
      sx={{
        flex: 1,
        minHeight: { xs: '60vh', md: '100vh' },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'background.default',
        p: { xs: 3, md: 4 }
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 600,
            mb: 1,
            textAlign: 'center',
            color: 'text.primary'
          }}
        >
          Welcome Back
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 4,
            textAlign: 'center',
            color: 'text.secondary'
          }}
        >
          Sign in to continue to Traffic Designer
        </Typography>
        
        <SignIn 
          appearance={{
            elements: {
              formButtonPrimary: {
                backgroundColor: '#c62828',
                '&:hover': {
                  backgroundColor: '#8e0000'
                },
                borderRadius: '8px',
                fontSize: '16px',
                padding: '12px 24px'
              },
              formFieldInput: {
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                '&:focus': {
                  borderColor: '#c62828',
                  boxShadow: '0 0 0 2px rgba(198, 40, 40, 0.1)'
                }
              },
              formFieldLabel: {
                color: '#424242',
                fontWeight: 500
              },
              card: {
                boxShadow: 'none',
                border: 'none'
              },
              headerTitle: {
                display: 'none'
              },
              headerSubtitle: {
                display: 'none'
              },
              socialButtonsBlockButton: {
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              },
              dividerLine: {
                backgroundColor: '#e0e0e0'
              },
              dividerText: {
                color: '#666666'
              }
            }
          }}
        />
      </Box>
    </Box>
  </Box>
);

// Main App Content
function AuthenticatedApp() {
  const { plan = null, checkQuota, startTrial, getToken } = useAuth();
  
  // Project Management
  const {
    projectState,
    projectData,
    loadProject,
    createNewProject,
    saveProject,
    saveProjectAs,
    renameProject,
    updateProjectData,
    resetProject,
  } = useProject();
  
  const [showProjectSelector, setShowProjectSelector] = useState(true); // Show on app start
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const lastRestoredProjectIdRef = useRef<string | null>(null); // Track which project's map state we've restored
  const skipSyncCountRef = useRef<number>(0); // Skip N sync effect runs after loading to prevent marking dirty
  
  const [workzones, setWorkzones] = useState<any[]>([]); // Changed to array for multiple workzones
  const [worksiteLocation, setWorksiteLocation] = useState<WorksiteLocation | null>(null); // Searched address location
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState(false);
  // Removed sidebar state - no longer needed
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState<string | undefined>(undefined);
  const [mapboxAccessToken, setMapboxAccessToken] = useState<string | undefined>(undefined);
  const [mapProvider, setMapProvider] = useState<'google' | 'mapbox'>(
    (process.env.REACT_APP_MAP_PROVIDER as 'google' | 'mapbox') || 'google'
  );
  const [signLibrary, setSignLibrary] = useState<any | null>(null);
  const [overlays, setOverlays] = useState<any | null>(null);
  const [signPlacement, setSignPlacement] = useState<any | null>(null);
  const [cones, setCones] = useState<any[]>([]);
  const [isManualMode , setManualMode] = useState(true); // Default to manual mode
  const [printArea, setPrintArea] = useState<any | null>(null);
  const [isDrawingPrintArea, setDrawingPrintArea] = useState(false);
  const [currentTool, setCurrentTool] = useState<ToolMode>('cursor');
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'png' | 'jpeg'>('pdf');
  const [selectedResolution, setSelectedResolution] = useState<'512' | '1024'>('512');
  const [selectedPaperSize, setSelectedPaperSize] = useState<PaperSize>('A3');
  const [selectedOrientation, setSelectedOrientation] = useState<Orientation>('landscape');
  const [boundingBoxActive, setBoundingBoxActive] = useState(false);
  const [mapFadeOpacity, setMapFadeOpacity] = useState(0);
  const [showCompositeBuilder, setShowCompositeBuilder] = useState(false);
  const [customSignsReloadKey, setCustomSignsReloadKey] = useState(0); // Increment to force reload
  const [rectangleOverlay, setRectangleOverlay] = useState<google.maps.Rectangle | null>(null);
  const [isDrawingWorkzone, setIsDrawingWorkzone] = useState(false);
  const [arrowDraft, setArrowDraft] = useState<ArrowDraft | null>(null);
  const [arrows, setArrows] = useState<ArrowDescriptor[]>([]);
  const [signLines, setSignLines] = useState<SignLine[]>([]);
  const [autoArrowsEnabled, setAutoArrowsEnabled] = useState(false); // Disabled for now - manual arrows only
  const [delineatorDraft, setDelineatorDraft] = useState<DelineatorDraft | null>(null);
  const [delineatorLines, setDelineatorLines] = useState<DelineatorLine[]>([]);
  const [pageAnnotations, setPageAnnotations] = useState<PageAnnotation[]>([]);
  const [showAnnotationEditor, setShowAnnotationEditor] = useState(false);
  const [autoExportAnnotations, setAutoExportAnnotations] = useState(false);
  const [capturedMapCanvas, setCapturedMapCanvas] = useState<string | null>(null);
  const [capturedAnnotatedCanvas, setCapturedAnnotatedCanvas] = useState<string | null>(null);
  const [selectedElement, setSelectedElement] = useState<SelectedElement>(null);
  const [roadToolGeometry, setRoadToolGeometry] = useState<any | null>(null);
  const [roadToolVisible, setRoadToolVisible] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [complianceScore, setComplianceScore] = useState<number | null>(null);
  const [isCheckingCompliance, setIsCheckingCompliance] = useState(false);
  const handleArrowDistanceChange = useCallback((id: string | 'all', distance: number) => {
    setArrows((prev) => {
      const nextDistance = Number.isFinite(distance) ? distance : 0;
      if (id === 'all') {
        return prev.map((arrow) => ({ ...arrow, distance: nextDistance }));
      }
      return prev.map((arrow) => (arrow.id === id ? { ...arrow, distance: nextDistance } : arrow));
    });
  }, []);

  const handleArrowDelete = useCallback((id: string) => {
    setArrows((prev) => prev.filter((arrow) => arrow.id !== id));
  }, []);

  const handleArrowMove = useCallback((id: string, start: { lat: number; lng: number }, end: { lat: number; lng: number }) => {
    const distance = Math.round(haversineDistance(start.lat, start.lng, end.lat, end.lng) * 10) / 10;
    setArrows((prev) => prev.map((arrow) => 
      arrow.id === id 
        ? { ...arrow, start, end, distance, startSignId: undefined, endSignId: undefined } // Unlink from signs when moved manually
        : arrow
    ));
  }, []);

  // Find the nearest sign to a point (within threshold distance)
  const findNearestSign = useCallback((point: { lat: number; lng: number }, thresholdMeters: number = 15) => {
    const signs = signPlacement?.signs || [];
    let nearestSign: any = null;
    let nearestDistance = Infinity;

    for (const sign of signs) {
      const dist = haversineDistance(point.lat, point.lng, sign.lat, sign.lng);
      if (dist < thresholdMeters && dist < nearestDistance) {
        nearestSign = sign;
        nearestDistance = dist;
      }
    }

    return nearestSign;
  }, [signPlacement?.signs]);

  const handleArrowLabelVisibilityChange = useCallback((id: string, show: boolean) => {
    setArrows((prev) => prev.map((arrow) => (
      arrow.id === id ? { ...arrow, showDistanceLabel: show } : arrow
    )));
    setSelectedElement((prev) => {
      if (!prev || prev.type !== 'arrow' || prev.id !== id) {
        return prev;
      }
      return { ...prev, data: { ...prev.data, showDistanceLabel: show } };
    });
  }, []);

  // Delineator handlers
  const handleDelineatorDraftStart = useCallback((deviceType: DelineatorDraft['deviceType']) => {
    setDelineatorDraft({
      deviceType,
      path: [],
      spacing: DEFAULT_DELINEATOR_SPACING,
      deviceSize: DEFAULT_DELINEATOR_SIZE,
      geometryType: DEFAULT_DELINEATOR_GEOMETRY,
    });
  }, []);

  const handleDelineatorDraftAddPoint = useCallback((point: { lat: number; lng: number }) => {
    setDelineatorDraft((prev) => {
      if (!prev) return prev;
      return { ...prev, path: [...prev.path, point] };
    });
  }, []);

  const handleDelineatorComplete = useCallback((delineator: DelineatorInput) => {
    if (delineator.path.length < 2) {
      // Need at least 2 points to create a line
      setDelineatorDraft(null);
      return;
    }
    
    setDelineatorLines((prev) => [
      ...prev,
      {
        id: generateId(),
        deviceType: delineator.deviceType,
        path: delineator.path,
        spacing: delineator.spacing,
        deviceSize: delineator.deviceSize,
        geometryType: delineator.geometryType,
      },
    ]);
    setDelineatorDraft(null);
    setCurrentTool('cursor');
  }, []);

  const handleDelineatorDelete = useCallback((id: string) => {
    setDelineatorLines((prev) => prev.filter((line) => line.id !== id));
  }, []);

  const handleDelineatorUpdate = useCallback((id: string, updates: Partial<DelineatorLine>) => {
    setDelineatorLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, ...updates } : line))
    );
  }, []);

  // Element selection handler - selects element and auto-opens properties panel
  const handleElementSelect = useCallback((element: SelectedElement) => {
    setSelectedElement(element);
    if (element) {
      setActivePanel('properties');
    }
  }, []);

  // Clear selection when clicking empty area
  const handleClearSelection = useCallback(() => {
    setSelectedElement(null);
  }, []);

  // Auto-detect sign lines and sync auto arrows when signs change
  useEffect(() => {
    if (!autoArrowsEnabled || !signPlacement?.signs) {
      return;
    }

    // Debug: Log signs with edge metadata
    const signsWithEdge = (signPlacement.signs as PlacedSign[]).filter(s => s.edgeMetadata?.roadSegmentId);
    console.log('[AutoArrows] Signs with edge metadata:', signsWithEdge.length, '/', signPlacement.signs.length);
    signsWithEdge.forEach(s => {
      console.log(`  - Sign ${s.id}: side=${s.edgeMetadata?.side}, angle=${s.edgeMetadata?.edgeAngle}, road=${s.edgeMetadata?.roadSegmentId?.substring(0, 20)}`);
    });

    // Detect sign lines from current signs
    const detectedLines = detectSignLines(signPlacement.signs as PlacedSign[]);
    console.log('[AutoArrows] Detected sign lines:', detectedLines.length);
    detectedLines.forEach(line => {
      console.log(`  - Line ${line.id}: ${line.signIds.length} signs, road=${line.roadSegmentId?.substring(0, 20)}, side=${line.side}`);
    });
    setSignLines(detectedLines);

    // Sync auto arrows based on detected lines
    setArrows(prevArrows => {
      const newArrows = syncAutoArrows(detectedLines, signPlacement.signs as PlacedSign[], prevArrows);
      const autoCount = newArrows.filter(a => a.isAutoGenerated).length;
      console.log('[AutoArrows] Total arrows:', newArrows.length, '(auto:', autoCount, ')');
      return newArrows;
    });
  }, [signPlacement?.signs, autoArrowsEnabled]);

  const handleAreaSelected = React.useCallback((area: any) => {
    setPrintArea(area);
    setShowPrintAreaConfirmDialog(true);
    setCurrentTool('cursor');
    setDrawingPrintArea(false);
  }, []);

  // State for the new print area confirmed dialog
  const [showPrintAreaConfirmDialog, setShowPrintAreaConfirmDialog] = useState(false);


  const handleMapReady = React.useCallback((controls: any) => {
    setMapControls(controls);
  }, []);

  // Removed mode selection - defaulting to manual mode
  const [showTrafficChat, setShowTrafficChat] = useState(false);

  const [activePanel, setActivePanel] = useState<'library' | 'chat' | 'quota' | 'export' | 'properties' | null>(null);

  const [titleBoxData, setTitleBoxData] = useState<TitleBoxData>({
    useTitleBox: true,
    tcpNumber: '',
    client: '',
    roadType: 'TWO WAY - TWO LANE ROADS',
    contact: '',
    phone: '',
    location: '',
    council: '',
    startDate: '',
    finishDate: '',
    startTime: '',
    finishTime: '',
    author: '',
    createdOn: new Date().toLocaleDateString(),
    modifiedBy: '',
    modifiedOn: '',
    state: 'SA',
    lengthOfZone: '',
    roadSpeed: '50',
    logos: {
      left: null,
      center: null,
      right: null
    }
  });

  // Abstract map controls type - works with both Google Maps and Mapbox
  const [mapControls, setMapControls] = useState<{
    zoomIn: () => void;
    zoomOut: () => void;
    setDrawingMode: (mode: any) => void; // Abstract: accepts google.maps.drawing.OverlayType or 'polygon' | 'rectangle' | null
    snapToLocation: (location?: { lat: number; lng: number }) => void;
    getMapInstance: () => any; // Abstract: returns google.maps.Map or mapboxgl.Map
    captureCanvas?: (boundingBox?: { x: number; y: number; width: number; height: number }, fadeOpacity?: number) => Promise<string | null>; // Mapbox only: capture map as base64 data URL, optionally cropped to bounding box, with optional fade on map only
  } | null>(null);

  // Annotation Editor handlers
  const handleOpenAnnotationEditor = useCallback(async () => {
    // Capture the current map canvas before opening the editor
    if (mapControls?.captureCanvas && printArea) {
      try {
        // Pass mapFadeOpacity to apply fade overlay on the captured map
        const canvas = await mapControls.captureCanvas(undefined, mapFadeOpacity);
        setCapturedMapCanvas(canvas);
        setShowAnnotationEditor(true);
      } catch (e) {
        console.error('Failed to capture map canvas:', e);
        // Still open editor, just without the captured map
        setShowAnnotationEditor(true);
      }
    } else {
      setShowAnnotationEditor(true);
    }
  }, [mapControls, printArea, mapFadeOpacity]);

  const handleCloseAnnotationEditor = useCallback(() => {
    setShowAnnotationEditor(false);
    setAutoExportAnnotations(false);
  }, []);

  const handleAnnotationExport = useCallback((annotations: PageAnnotation[], annotatedCanvas: string | null) => {
    setPageAnnotations(annotations);
    setCapturedAnnotatedCanvas(annotatedCanvas);
    console.log('Annotation export received:', {
      annotationCount: annotations.length,
      hasAnnotatedCanvas: !!annotatedCanvas,
    });
    // Sync title box state from annotations back to App state
    const titleBoxAnnotation = annotations.find((a: any) => a.type === 'title-box');
    if (!titleBoxAnnotation) {
      // Title box was deleted in annotation editor — disable it
      setTitleBoxData(prev => ({ ...prev, useTitleBox: false }));
    } else if ((titleBoxAnnotation as any).useTitleBox === false) {
      // Title box was toggled off in annotation editor
      setTitleBoxData(prev => ({ ...prev, useTitleBox: false }));
    }
    // Trigger export with annotations
    setShowAnnotationEditor(false);
    setAutoExportAnnotations(false);
    setShowFormatDialog(true);
  }, []);

  // ========== PROJECT MANAGEMENT HANDLERS ==========
  
  // Serialize current state to project data
  const serializeProjectData = useCallback((): ProjectData => {
    const mapInstance = mapControls?.getMapInstance?.();
    let mapState: StoredMapState | undefined;
    
    if (mapInstance) {
      try {
        const center = mapInstance.getCenter();
        mapState = {
          center: { lat: center.lat(), lng: center.lng() },
          zoom: mapInstance.getZoom(),
          bearing: mapInstance.getBearing?.() || 0,
          pitch: mapInstance.getPitch?.() || 0,
        };
      } catch (e) {
        // Mapbox has different API
        try {
          const center = mapInstance.getCenter();
          mapState = {
            center: { lat: center.lat, lng: center.lng },
            zoom: mapInstance.getZoom(),
            bearing: mapInstance.getBearing?.() || 0,
            pitch: mapInstance.getPitch?.() || 0,
          };
        } catch (e2) {
          console.warn('Could not serialize map state');
        }
      }
    }
    
    return {
      mapState,
      worksiteLocation: worksiteLocation || undefined,
      signs: signPlacement?.signs || [],
      workzones: workzones.map(wz => ({
        id: wz.id,
        polygon: wz.polygon,
        location: wz.location,
        address: wz.address,
        name: wz.name,
        city: wz.city,
        state: wz.state,
        country: wz.country,
      })),
      arrows,
      delineatorLines,
      printArea: printArea ? {
        bounds: printArea.bounds,
        screenBounds: printArea.screenBounds,
      } : undefined,
      paperSize: selectedPaperSize,
      orientation: selectedOrientation,
      mapFadeOpacity,
      annotations: pageAnnotations,
      titleBoxData: titleBoxData ? {
        ...titleBoxData,
        useTitleBox: titleBoxData.useTitleBox ?? true,
      } : undefined,
    };
  }, [mapControls, signPlacement, workzones, arrows, delineatorLines, printArea, selectedPaperSize, selectedOrientation, mapFadeOpacity, pageAnnotations, titleBoxData, worksiteLocation]);

  // Load project data into current state
  const loadProjectData = useCallback((data: ProjectData, projectId?: string | null) => {
    // Load signs
    if (data.signs && data.signs.length > 0) {
      setSignPlacement({ signs: data.signs });
    } else {
      setSignPlacement(null);
    }
    
    // Load workzones
    setWorkzones(data.workzones || []);
    
    // Load arrows
    setArrows(data.arrows || []);
    
    // Load delineator lines
    setDelineatorLines(data.delineatorLines || []);
    
    // Load print settings
    if (data.printArea) {
      setPrintArea(data.printArea);
    } else {
      setPrintArea(null);
    }
    setSelectedPaperSize(data.paperSize || 'A3');
    setSelectedOrientation(data.orientation || 'landscape');
    setMapFadeOpacity(data.mapFadeOpacity || 0);
    
    // Load annotations
    setPageAnnotations(data.annotations || []);
    
    // Load title box data
    if (data.titleBoxData) {
      setTitleBoxData(data.titleBoxData);
    }
    
    // Load worksite location (searched address)
    if (data.worksiteLocation) {
      setWorksiteLocation(data.worksiteLocation);
    } else {
      setWorksiteLocation(null);
    }
  }, []);

  // Handle new project selection
  const handleNewProject = useCallback(async () => {
    setShowProjectSelector(true);
    // Reset current state
    setSignPlacement(null);
    setWorkzones([]);
    setArrows([]);
    setDelineatorLines([]);
    setPrintArea(null);
    setPageAnnotations([]);
    setWorksiteLocation(null); // Clear worksite for new project
    lastRestoredProjectIdRef.current = null; // Reset so map position will be restored on next load
    skipSyncCountRef.current = 0; // Clear skip count
    // Note: lastProjectIdRef will be updated by the load effect when new project is created
    resetProject();
    
    // Reset map to default location (Sydney, Australia)
    const mapInstance = mapControls?.getMapInstance();
    if (mapInstance) {
      const defaultCenter = { lat: -33.8688, lng: 151.2093 };
      if (typeof mapInstance.flyTo === 'function') {
        // Mapbox GL JS
        mapInstance.flyTo({
          center: [defaultCenter.lng, defaultCenter.lat],
          zoom: 5,
          duration: 1000,
        });
      } else if (typeof mapInstance.setCenter === 'function') {
        // Google Maps
        mapInstance.setCenter(defaultCenter);
        mapInstance.setZoom(5);
      }
    }
  }, [resetProject, mapControls]);

  // Handle project selected from selector
  const handleProjectSelect = useCallback(async (projectId: string) => {
    try {
      await loadProject(projectId);
      // Project data will be in projectData from useProject hook
      setShowProjectSelector(false);
    } catch (error) {
      console.error('Failed to load project:', error);
    }
  }, [loadProject]);

  // Handle new project created
  const handleNewProjectCreated = useCallback((projectId: string, projectName: string) => {
    setShowProjectSelector(false);
    setWorksiteLocation(null); // Clear worksite for new project (redundant but ensures consistency)
    // State is already reset from handleNewProject, just close the dialog
  }, []);

  // Save current project
  const handleSaveProject = useCallback(async () => {
    if (!projectState.currentProjectId) {
      // No project yet - show save as dialog
      setShowSaveAsDialog(true);
      return;
    }
    
    const data = serializeProjectData();
    try {
      await saveProject(data);
    } catch (error) {
      console.error('Failed to save project:', error);
    }
  }, [projectState.currentProjectId, serializeProjectData, saveProject]);

  // Save project as new
  const handleSaveProjectAs = useCallback(async () => {
    if (!saveAsName.trim()) return;
    
    const data = serializeProjectData();
    try {
      updateProjectData(data);
      await saveProjectAs(saveAsName.trim());
      setShowSaveAsDialog(false);
      setSaveAsName('');
    } catch (error) {
      console.error('Failed to save project as:', error);
    }
  }, [saveAsName, serializeProjectData, updateProjectData, saveProjectAs]);

  // Track last loaded project to detect project changes
  const lastProjectIdRef = useRef<string | null>(null);
  
  // Load project data when projectData or project ID changes (from useProject hook)
  useEffect(() => {
    // Only load if we have a project ID
    if (!projectState.currentProjectId) return;
    
    // Detect if this is a new/different project being loaded
    const isNewProject = lastProjectIdRef.current !== projectState.currentProjectId;
    
    if (isNewProject) {
      // Skip the next 2 sync effect runs to prevent marking dirty
      // (one for the initial effect run, one for when local state updates)
      skipSyncCountRef.current = 2;
      lastProjectIdRef.current = projectState.currentProjectId;
      
      // Always load project data when switching projects
      // This ensures local state matches the loaded project (even if empty)
      loadProjectData(projectData);
    }
  }, [projectData, loadProjectData, projectState.currentProjectId]);

  // Restore map position when project is loaded (fallback for mapState when no worksiteLocation)
  // Note: worksiteLocation is now handled by the map component via loadedWorksiteLocation prop
  useEffect(() => {
    if (!mapControls || !projectState.currentProjectId) return;
    if (lastRestoredProjectIdRef.current === projectState.currentProjectId) return;
    
    const mapInstance = mapControls.getMapInstance();
    if (!mapInstance) return;
    
    // If there's a worksiteLocation, the map component handles it via the prop
    // This effect only handles the fallback to mapState.center
    if (projectData?.worksiteLocation) {
      lastRestoredProjectIdRef.current = projectState.currentProjectId;
      return;
    }
    
    // Fallback to mapState.center (for projects saved before worksiteLocation was added)
    const location = projectData?.mapState?.center;
    const zoom = projectData?.mapState?.zoom || 18;
    
    if (location) {
      lastRestoredProjectIdRef.current = projectState.currentProjectId;
      
      if (typeof mapInstance.flyTo === 'function') {
        mapInstance.flyTo({ center: [location.lng, location.lat], zoom, duration: 1500 });
      } else if (typeof mapInstance.setCenter === 'function') {
        mapInstance.setCenter({ lat: location.lat, lng: location.lng });
        mapInstance.setZoom(zoom);
      }
    } else if (projectData?.signs?.length > 0 || projectData?.workzones?.length > 0) {
      lastRestoredProjectIdRef.current = projectState.currentProjectId;
    }
  }, [projectData, mapControls, projectState.currentProjectId]);

  // Sync project data when state changes (for auto-save)
  // Skip when loading a project to prevent immediately marking as dirty
  useEffect(() => {
    if (!projectState.currentProjectId) return;
    
    // Skip syncing if we're still in the loading phase
    // This prevents marking dirty immediately after loading
    if (skipSyncCountRef.current > 0) {
      skipSyncCountRef.current--;
      return;
    }
    
    const data = serializeProjectData();
    updateProjectData(data);
  }, [signPlacement, workzones, arrows, delineatorLines, printArea, pageAnnotations, titleBoxData, worksiteLocation, projectState.currentProjectId, serializeProjectData, updateProjectData]);

  // Save annotation template handler
  const handleSaveAnnotationTemplate = useCallback(async (
    name: string,
    description: string,
    annotations: PageAnnotation[]
  ) => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      
      const { saveAnnotationTemplate } = await import('./services/projects');
      await saveAnnotationTemplate(token, {
        name,
        description,
        annotations,
        paperSize: selectedPaperSize,
        orientation: selectedOrientation,
      });
    } catch (error) {
      console.error('Failed to save annotation template:', error);
      throw error;
    }
  }, [getToken, selectedPaperSize, selectedOrientation]);

  // Handle compliance check from the tracker button
  const handleCheckCompliance = useCallback(async () => {
    if (isCheckingCompliance) return;
    
    setIsCheckingCompliance(true);
    
    try {
      const token = await getToken();
      
      // Build context
      const context = {
        signs: signPlacement?.signs || [],
        workzones: workzones || [],
        signCount: signPlacement?.signs?.length || 0,
        workzoneCount: workzones?.length || 0,
        hasWorkzone: workzones && workzones.length > 0,
        address: workzones?.[0]?.address || null,
        location: workzones?.[0]?.location || null,
        state: workzones?.[0]?.state || null,
      };
      
      // Capture canvas for vision analysis
      let capturedImage: string | null = null;
      if (mapControls?.captureCanvas) {
        try {
          capturedImage = await mapControls.captureCanvas(undefined, mapFadeOpacity);
          console.log('Captured canvas for compliance check:', capturedImage ? 'success' : 'failed');
        } catch (e) {
          console.warn('Could not capture canvas:', e);
        }
      }
      
      const headers: HeadersInit = token
        ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
        : { 'Content-Type': 'application/json' };
      
      const response = await fetch(resolveApiUrl('/api/traffic-specialist/analyze'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ context, image: capturedImage }),
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.score !== null && result.score !== undefined) {
          setComplianceScore(result.score);
        }
      }
    } catch (error) {
      console.error('Compliance check error:', error);
    } finally {
      setIsCheckingCompliance(false);
    }
  }, [getToken, signPlacement, workzones, mapControls, mapFadeOpacity, isCheckingCompliance]);

  // Fetch configuration (e.g., Google Maps API key) once on mount
  useEffect(() => {
  const fetchConfig = async () => {
      try {
        const res = await fetch(resolveApiUrl('/api/config'));
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const data = await res.json();
        if (data.googleMapsApiKey) {
          setGoogleMapsApiKey(data.googleMapsApiKey);
        } else {
          console.error('googleMapsApiKey missing from /api/config response:', data);
        }
        if (data.mapboxAccessToken) {
          setMapboxAccessToken(data.mapboxAccessToken);
        }
      } catch (error) {
        console.error("Failed to fetch app config:", error);
        setError("Could not connect to the server to fetch configuration.");
        setConnectionError(true);
      }
    };
    const fetchSignLibrary = async () => {
      try {
        const library = await getSignLibrary();
        setSignLibrary(library);
      } catch (error) {
        console.error("Failed to fetch sign library:", error);
        setConnectionError(true);
      }
    };

    fetchConfig();
    fetchSignLibrary();
  }, []);

  const hasWorkzonePolygon = workzones.length > 0 && 
  workzones.some(wz => wz.polygon && wz.polygon.length >= 3);

  const handleWorkzoneSelect = async (data: any) => {
    // Save worksite location when user searches/selects a location (address search)
    if (data.location && data.address) {
      setWorksiteLocation({
        lat: data.location.lat,
        lng: data.location.lng,
        address: data.address,
        name: data.name,
        city: data.city,
        state: data.state,
        country: data.country,
      });
    }
    
    // Add new workzone to array instead of replacing
    const newWorkzone = { ...data, id: `wz-${Date.now()}` };
    setWorkzones(prev => [...prev, newWorkzone]);
    
    // Auto-generate road tool if in Pro Mode (has AI generation access)
    if (plan?.ai_generation && data.polygon && data.polygon.length > 0) {
      try {
        setIsLoading(true);
        const roadTool = await generateRoadTool(data.polygon, 500, data.state);
        setRoadToolGeometry(roadTool);
        setRoadToolVisible(true);
        console.log('Road tool generated:', roadTool.metadata);
      } catch (error: any) {
        console.log('Failed to generate road tool:', error);
        setError('Failed to generate road tool. You can continue with manual placement.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSignMove = (signId: string, newLatLng: { lat: number; lng: number }, edgeMetadata?: SignEdgeMetadata) => {
    setSignPlacement((prevPlacement: any) => {
      if (!prevPlacement || !prevPlacement.signs) return prevPlacement;

      const updatedSigns = prevPlacement.signs.map((sign: any) => {
        if (sign.id === signId) {
          const movedSign = { 
            ...sign, 
            lat: newLatLng.lat, 
            lng: newLatLng.lng,
            // Store edge metadata if provided (from snap-to-edge)
            ...(edgeMetadata && { edgeMetadata })
          };
          return movedSign;
        }
        return sign;
      });

      return { ...prevPlacement, signs: updatedSigns };
    });

    // Update any arrows connected to the moved sign
    setArrows((prevArrows) => 
      prevArrows.map((arrow) => {
        if (arrow.startSignId === signId) {
          // Recalculate distance
          const dist = haversineDistance(newLatLng.lat, newLatLng.lng, arrow.end.lat, arrow.end.lng);
          return { 
            ...arrow, 
            start: { lat: newLatLng.lat, lng: newLatLng.lng },
            distance: Math.round(dist * 10) / 10,
          };
        }
        if (arrow.endSignId === signId) {
          // Recalculate distance
          const dist = haversineDistance(arrow.start.lat, arrow.start.lng, newLatLng.lat, newLatLng.lng);
          return { 
            ...arrow, 
            end: { lat: newLatLng.lat, lng: newLatLng.lng },
            distance: Math.round(dist * 10) / 10,
          };
        }
        return arrow;
      })
    );
  };

  const handleRemoveSign = (signId: string) => {
    setSignPlacement((prevPlacement: any) => {
      if (!prevPlacement || !prevPlacement.signs) {
        return prevPlacement;
      }
      
      const updatedSigns = prevPlacement.signs.filter((sign: any) => sign.id !== signId);
      return { ...prevPlacement, signs: updatedSigns };
    });
  };

  const handleSignResize = (signId: string, newScale: number) => {
    setSignPlacement((prevPlacement: any) => {
      if (!prevPlacement || !prevPlacement.signs) {
        return prevPlacement;
      }
      
      const updatedSigns = prevPlacement.signs.map((sign: any) => {
        if (sign.id === signId) {
          return { ...sign, scale: newScale };
        }
        return sign;
      });
      
      return { ...prevPlacement, signs: updatedSigns };
    });
  };

  const handleSignRotate = (signId: string, newRotation: number) => {
    setSignPlacement((prevPlacement: any) => {
      if (!prevPlacement || !prevPlacement.signs) {
        return prevPlacement;
      }
      
      const updatedSigns = prevPlacement.signs.map((sign: any) => {
        if (sign.id === signId) {
          return { ...sign, rotation: newRotation };
        }
        return sign;
      });
      
      return { ...prevPlacement, signs: updatedSigns };
    });
  };

  // Handle sign callout offset change (for repositioning the visual label without moving the sign)
  const handleSignCalloutMove = (signId: string, offset: { x: number; y: number }) => {
    setSignPlacement((prevPlacement: any) => {
      if (!prevPlacement || !prevPlacement.signs) {
        return prevPlacement;
      }
      
      const updatedSigns = prevPlacement.signs.map((sign: any) => {
        if (sign.id === signId) {
          return { ...sign, calloutOffset: offset };
        }
        return sign;
      });
      
      return { ...prevPlacement, signs: updatedSigns };
    });
  };

  const handleAddNewSign = (
    signCode: string, 
    newLatLng: { lat: number; lng: number }, 
    rotation?: number, 
    composite?: any,
    edgeMetadata?: SignEdgeMetadata
  ) => {
    // ALWAYS generate a unique ID for each sign instance, even for composites
    // This prevents React key collisions when multiple instances of the same composite are placed
    const uniqueId = `manual-${signCode}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const newSign: any = {
      id: uniqueId,
      lat: newLatLng.lat,
      lng: newLatLng.lng,
      sign: {
        code_sa: signCode,
      },
      position: {
        lane: 'manual',
        ref: 'manual',
      },
      rotation: rotation !== undefined ? rotation : 0,
      // Store edge metadata if provided (from snap-to-edge)
      ...(edgeMetadata && { edgeMetadata }),
    };

    // Add composite data if provided (store original composite ID separately)
    if (composite) {
      newSign.composite = {
        ...composite,
        originalId: composite.id, // Store original composite ID for reference
      };
    }

    setSignPlacement((prevPlacement: any) => {
      if (!prevPlacement || !prevPlacement.signs) {
        return { signs: [newSign] };
      }
      return { ...prevPlacement, signs: [...prevPlacement.signs, newSign] };
    });
  };

  const handleSaveComposite = (composite: CompositeSign) => {
    // Get map center for placement (or you could drop it manually)
    const centerLat = mapControls?.getMapInstance()?.getCenter()?.lat() || -33.865143;
    const centerLng = mapControls?.getMapInstance()?.getCenter()?.lng() || 151.209900;

    const compositeSign = {
      id: composite.id,
      lat: centerLat,
      lng: centerLng,
      sign: {
        code_sa: 'COMPOSITE', // Special marker for composite signs
      },
      position: {
        lane: 'manual',
        ref: 'manual',
      },
      rotation: 0,
      composite: composite, // Store full composite data
      scale: 1.0,
    };


    setSignPlacement((prevPlacement: any) => {
      if (!prevPlacement || !prevPlacement.signs) {
        return { signs: [compositeSign] };
      }
      return { ...prevPlacement, signs: [...prevPlacement.signs, compositeSign] };
    });

    setShowCompositeBuilder(false);
  };

  const handleExportWithFormat = React.useCallback(async (format: 'pdf' | 'png' | 'jpeg', resolution: '512' | '1024') => {
    
    if (!printArea) {
      const errorMsg = 'Please select a print area and ensure signs are loaded.';
      console.error('Export validation failed:', { printArea: !!printArea, signs: signPlacement?.signs?.length });
      setError(errorMsg);
      return;
    }

    // Capture current camera state from map
    let tilt = 0;
    let heading = 0;
    let zoom = 18;
    let isMapbox = false;
    let capturedMapImage: string | null = null;
    
    if (mapControls) {
      const mapInstance = mapControls.getMapInstance();
      if (mapInstance) {
        // Detect if it's Mapbox (has getPitch/getBearing) or Google Maps (has getTilt/getHeading)
        if (typeof mapInstance.getPitch === 'function') {
          // Mapbox GL
          isMapbox = true;
          tilt = mapInstance.getPitch() || 0;
          heading = mapInstance.getBearing() || 0;
          zoom = mapInstance.getZoom() || 18;
        } else if (typeof mapInstance.getTilt === 'function') {
          // Google Maps
          tilt = mapInstance.getTilt() || 0;
          heading = mapInstance.getHeading() || 0;
          zoom = mapInstance.getZoom() || 18;
        }
      }
    }

    const highQuality = resolution === '1024';
    setIsLoading(true);
    setShowFormatDialog(false);
    
    // Get paper dimensions based on selected size and orientation
    const paperDimensions = getPaperDimensions(selectedPaperSize, selectedOrientation);
    console.log(`Paper dimensions: ${paperDimensions.pixelWidth}x${paperDimensions.pixelHeight} px (${selectedPaperSize} ${selectedOrientation})`);
    
    try {
        // For Mapbox, capture the canvas directly for WYSIWYG export
        // Skip canvas capture for high quality - use tile fetching for higher resolution
        if (isMapbox && mapControls?.captureCanvas && !highQuality) {
          console.log('Capturing Mapbox canvas for standard quality export...');
          
          // Get screen bounds from printArea if available (for cropping to bounding box)
          const screenBounds = (printArea as any)?.screenBounds as { x: number; y: number; width: number; height: number } | undefined;
          
          if (screenBounds) {
            console.log(`Cropping canvas to bounding box: ${screenBounds.width}x${screenBounds.height} at (${screenBounds.x}, ${screenBounds.y}), fade: ${mapFadeOpacity}`);
            capturedMapImage = await mapControls.captureCanvas(screenBounds, mapFadeOpacity);
          } else {
            console.log('No screen bounds available, capturing full canvas');
            capturedMapImage = await mapControls.captureCanvas(undefined, mapFadeOpacity);
          }
          
          if (capturedMapImage) {
            console.log('Canvas captured successfully');
          } else {
            console.warn('Canvas capture returned null, falling back to tile fetching');
          }
        } else if (highQuality) {
          console.log('High quality selected - using tile fetching for maximum resolution');
        }
        
        const payloadTitleBox = titleBoxData?.useTitleBox ? titleBoxData : undefined;
        // Send all workzones for export
        const workzonePayload = workzones.length > 0 
          ? workzones.map(wz => wz.polygon).filter(Boolean)
          : undefined;
        // Frontend sign base sizes - these match WorkzoneMap.tsx marker rendering
        const SIGN_BASE_SIZE = 32;      // Regular signs: baseSize in createSignIcon
        const COMPOSITE_BASE_SIZE = 64; // Composite signs: baseSize in createCompositeIcon
        
        const blob = await exportPlan(
          signPlacement.signs || [],
          printArea,
          zoom,
          format,
          payloadTitleBox,
          highQuality,
          workzonePayload,
          arrows,
          tilt,
          heading,
          SIGN_BASE_SIZE,
          COMPOSITE_BASE_SIZE,
          paperDimensions.pixelWidth,
          paperDimensions.pixelHeight,
          isMapbox ? 'mapbox' : 'google',
          capturedMapImage,
          mapFadeOpacity,
          capturedAnnotatedCanvas  // Full page with annotations from annotation editor
        );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `traffic-plan.${format}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (err: any) {
      const errorMsg = `Failed to export ${format.toUpperCase()}: ${err?.message || err}`;
      console.error('Export error:', err);
      console.error('Error stack:', err?.stack);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
      setDrawingPrintArea(false);
      setPrintArea(null);
      setBoundingBoxActive(false);
    }
  }, [printArea, signPlacement, titleBoxData, workzones, arrows, mapControls, selectedPaperSize, selectedOrientation, capturedAnnotatedCanvas]);



  const handleToggleManualMode = () => {
    setManualMode(!isManualMode);
  };

function handleRectangleComplete(rectangle: google.maps.Rectangle) {
    if (rectangleOverlay) {
      rectangleOverlay.setMap(null);
    }
    setRectangleOverlay(rectangle);
  }

  const handleToolChange = (tool: ToolMode) => {
  if (tool === 'boundingbox') {
    const togglingOff = currentTool === 'boundingbox';
    setCurrentTool(togglingOff ? 'cursor' : 'boundingbox');
    setBoundingBoxActive(!togglingOff);
    setDrawingPrintArea(!togglingOff);
    setIsDrawingWorkzone(false);
    
    if (rectangleOverlay) {
      rectangleOverlay.setMap(null);
      setRectangleOverlay(null);
    }
    
    // Use Google's Drawing Manager rectangle mode
    mapControls?.setDrawingMode(
      togglingOff ? null : window.google?.maps.drawing.OverlayType.RECTANGLE
    );
    return;
  }

  if (tool === 'workzone') {
    const togglingOff = currentTool === 'workzone';
    setCurrentTool(togglingOff ? 'cursor' : 'workzone');
    setIsDrawingWorkzone(!togglingOff);
    setBoundingBoxActive(false);
    setDrawingPrintArea(false);

    if (rectangleOverlay) {
      rectangleOverlay.setMap(null);
      setRectangleOverlay(null);
    }
    
    // Use POLYGON mode for workzones (free-form drawing)
    mapControls?.setDrawingMode(
      togglingOff ? null : window.google?.maps.drawing.OverlayType.POLYGON
    );
    
    return;
  }

  if (tool === 'arrow') {
    const togglingOff = currentTool === 'arrow';
    setCurrentTool(togglingOff ? 'cursor' : 'arrow');
    setBoundingBoxActive(false);
    setDrawingPrintArea(false);
    setIsDrawingWorkzone(false);
    if (rectangleOverlay) {
      rectangleOverlay.setMap(null);
      setRectangleOverlay(null);
    }
    if (togglingOff) {
      setArrowDraft(null);
    }
    return;
  }

  setCurrentTool(tool);
  setBoundingBoxActive(false);
  setDrawingPrintArea(false);
  setIsDrawingWorkzone(false);

  if (rectangleOverlay) {
    rectangleOverlay.setMap(null);
    setRectangleOverlay(null);
  }
  mapControls?.setDrawingMode(null);
};



  const handleSnapToWorkzone = () => {
    if (workzones.length > 0 && workzones[0]?.location) {
      // This will be implemented to snap the map to the first workzone area
      // TODO: Add map reference and pan/zoom to workzone location
    } else {
    }
  };
  const handleSignsGenerated = useCallback((newSigns: any[]) => {
    // Clean existing signs but keep workzones
    // Set signPlacement to the new signs from backend
    setSignPlacement({
      signs: newSigns
    });
    
    // Optionally fit map bounds to show all signs and workzones
    if (mapControls && newSigns.length > 0) {
      const mapInstance = mapControls.getMapInstance();
      if (mapInstance) {
        const bounds = new window.google.maps.LatLngBounds();
        
        // Include workzones in bounds
        workzones.forEach(wz => {
          if (wz.polygon) {
            wz.polygon.forEach((coord: any) => {
              bounds.extend({ lat: coord.lat, lng: coord.lng });
            });
          }
        });
        
        // Include new signs in bounds
        newSigns.forEach(sign => {
          if (sign.lat != null && sign.lng != null) {
            bounds.extend({ lat: sign.lat, lng: sign.lng });
          }
        });
        
        mapInstance.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      }
    }
  }, [mapControls, workzones]);


  if (connectionError) {
    return <Box sx={{ height: '100vh', bgcolor: 'background.default' }} />;
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', flexDirection: 'column' }}>

      {/* File Menu - Fixed Position Top Left */}
      <Box
        sx={{
          position: 'fixed',
          top: 12,
          left: 12,
          zIndex: 1200,
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          px: 1,
          py: 0.5,
        }}
      >
        <FileMenu
          projectName={projectState.currentProjectName}
          isDirty={projectState.isDirty}
          autoSaveStatus={projectState.autoSaveStatus}
          lastSavedAt={projectState.lastSavedAt}
          onNewProject={handleNewProject}
          onOpenProject={() => setShowProjectSelector(true)}
          onSave={handleSaveProject}
          onSaveAs={() => {
            setSaveAsName(projectState.currentProjectName);
            setShowSaveAsDialog(true);
          }}
          onRename={renameProject}
        />
      </Box>

      {/* Project Selector Dialog */}
      <ProjectSelector
        open={showProjectSelector}
        onClose={() => setShowProjectSelector(false)}
        onProjectSelect={handleProjectSelect}
        onNewProject={handleNewProjectCreated}
      />

      {/* Save As Dialog */}
      <Dialog
        open={showSaveAsDialog}
        onClose={() => setShowSaveAsDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Save Project As</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Project Name"
            value={saveAsName}
            onChange={(e) => setSaveAsName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && saveAsName.trim()) {
                handleSaveProjectAs();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveAsDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveProjectAs}
            disabled={!saveAsName.trim()}
            sx={{ textTransform: 'none' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Draggable Sign Library - Top Section */}
      <DraggableSignLibrary
        key={customSignsReloadKey}
        activePanel={activePanel}
        onOpenLibrary={() => setActivePanel(prev => (prev === 'library' ? null : 'library'))}
        onOpenTrafficSpecialist={() => setActivePanel(prev => (prev === 'chat' ? null : 'chat'))}
        onOpenProperties={() => setActivePanel(prev => (prev === 'properties' ? null : 'properties'))}
        onOpenQuota={() => setActivePanel(prev => (prev === 'quota' ? null : 'quota'))}
        onClosePanel={() => {
          setActivePanel(null);
          setSelectedElement(null);
        }}
        onOpenExport={() => setActivePanel(prev => (prev === 'export' ? null : 'export'))}
        signPlacement={signPlacement}
        workzones={workzones}
        arrows={arrows}
        onArrowDistanceChange={handleArrowDistanceChange}
        onArrowLabelVisibilityChange={handleArrowLabelVisibilityChange}
        onArrowDelete={handleArrowDelete}
        currentTool={currentTool}
        onDelineatorToolActivate={(deviceType) => {
          setCurrentTool('delineator');
          handleDelineatorDraftStart(deviceType);
        }}
        onDelineatorToolDeactivate={() => {
          setCurrentTool('cursor');
          setDelineatorDraft(null);
        }}
        delineatorLines={delineatorLines}
        onDelineatorDelete={handleDelineatorDelete}
        onDelineatorUpdate={handleDelineatorUpdate}
        selectedElement={selectedElement}
        onSignRotate={handleSignRotate}
        onSignResize={handleSignResize}
        onSignMove={handleSignMove}
        onSignCalloutMove={handleSignCalloutMove}
        onSignDelete={handleRemoveSign}
        onWorkzoneDelete={(id: string) => setWorkzones(prev => prev.filter(wz => wz.id !== id))}
      />

      {/* Top Right Controls */}
      <BottomLeftControls
        onOpenQuota={() => setActivePanel(prev => (prev === 'quota' ? null : 'quota'))}
        onOpenCompositeBuilder={() => setShowCompositeBuilder(true)}
        quotaCount={76}
        onCheckCompliance={handleCheckCompliance}
        complianceScore={complianceScore}
        isCheckingCompliance={isCheckingCompliance}
      />

      {/* Bottom Right Tools */}
      <BottomRightTools
        currentTool={currentTool}
        onToolChange={handleToolChange}
        onSnapToWorkzone={() => {
          if (mapControls) {
            if (workzones.length > 0 && workzones[0]?.location) {
              mapControls.snapToLocation(workzones[0].location);
            } else {
              mapControls.snapToLocation();
            }
          }
        }}
        onClearWorkzone={() => {
          if (!mapControls) return;
          setWorkzones([]); // Clear all workzones
          setIsDrawingWorkzone(false);
          if (mapControls.setDrawingMode) {
            mapControls.setDrawingMode(null);
          }
        }}
        hasWorkzone={workzones.length > 0}
        onCaptureSnapshot={() => {
          // Placeholder snapshot handler – integrate export later
        }}
      />
      
      {/* Full-width main content - maps span full screen */}
      <Box sx={{ flexGrow: 1, position: 'relative', width: '100%', height: '100vh' }}>
        
        {/* Conditional map provider rendering */}
        {mapProvider === 'google' ? (
          <WorkzoneMap
            apiKey={googleMapsApiKey}
            onWorkzoneSelect={handleWorkzoneSelect}
            overlays={overlays}
            signPlacement={signPlacement}
            cones={cones}
            signLibrary={signLibrary}
            isManualMode={isManualMode}
            onSignMove={handleSignMove}
            onSignAdd={handleAddNewSign}
            onSignRemove={handleRemoveSign}
            onSignResize={handleSignResize}
            onSignRotate={handleSignRotate}
            onSignCalloutMove={handleSignCalloutMove}
            isDrawingPrintArea={isDrawingPrintArea}
            isDrawingWorkzone={isDrawingWorkzone}
            onPrintAreaSelect={setPrintArea}
            onMapReady={handleMapReady}
            onAreaSelected={handleAreaSelected}
            currentTool={currentTool}
            arrowDraft={arrowDraft}
            arrows={arrows}
            onArrowDraftStart={(point) => setArrowDraft({ start: point })}
            onArrowComplete={(arrow) => {
              // Find nearby signs to link arrow endpoints
              const startSign = findNearestSign(arrow.start);
              const endSign = findNearestSign(arrow.end);
              
              // Snap to sign positions if found
              const finalStart = startSign ? { lat: startSign.lat, lng: startSign.lng } : arrow.start;
              const finalEnd = endSign ? { lat: endSign.lat, lng: endSign.lng } : arrow.end;
              
              // Recalculate distance with snapped positions
              const finalDistance = startSign || endSign 
                ? Math.round(haversineDistance(finalStart.lat, finalStart.lng, finalEnd.lat, finalEnd.lng) * 10) / 10
                : arrow.distance;
              
              setArrows((prev) => [
                ...prev,
                {
                  id: generateId(),
                  start: finalStart,
                  end: finalEnd,
                  distance: finalDistance,
                  showDistanceLabel: true,
                  startSignId: startSign?.id,
                  endSignId: endSign?.id,
                },
              ]);
              setArrowDraft(null);
            }}
            onArrowDistanceChange={handleArrowDistanceChange}
            onArrowDelete={handleArrowDelete}
            workzones={workzones}
            roadToolGeometry={roadToolGeometry}
            roadToolVisible={roadToolVisible}
            isProMode={plan?.ai_generation || false}
          />
        ) : (
          <WorkzoneMapMapbox
            apiKey={mapboxAccessToken}
            onWorkzoneSelect={handleWorkzoneSelect}
            overlays={overlays}
            signPlacement={signPlacement}
            cones={cones}
            signLibrary={signLibrary}
            isManualMode={isManualMode}
            onSignMove={handleSignMove}
            onSignAdd={handleAddNewSign}
            onSignRemove={handleRemoveSign}
            onSignResize={handleSignResize}
            onSignRotate={handleSignRotate}
            onSignCalloutMove={handleSignCalloutMove}
            isDrawingPrintArea={isDrawingPrintArea}
            isDrawingWorkzone={isDrawingWorkzone}
            onPrintAreaSelect={setPrintArea}
            onMapReady={handleMapReady}
            onAreaSelected={handleAreaSelected}
            currentTool={currentTool}
            arrowDraft={arrowDraft}
            arrows={arrows}
            onArrowDraftStart={(point) => setArrowDraft({ start: point })}
            onArrowComplete={(arrow) => {
              // Find nearby signs to link arrow endpoints
              const startSign = findNearestSign(arrow.start);
              const endSign = findNearestSign(arrow.end);
              
              // Snap to sign positions if found
              const finalStart = startSign ? { lat: startSign.lat, lng: startSign.lng } : arrow.start;
              const finalEnd = endSign ? { lat: endSign.lat, lng: endSign.lng } : arrow.end;
              
              // Recalculate distance with snapped positions
              const finalDistance = startSign || endSign 
                ? Math.round(haversineDistance(finalStart.lat, finalStart.lng, finalEnd.lat, finalEnd.lng) * 10) / 10
                : arrow.distance;
              
              setArrows((prev) => [
                ...prev,
                {
                  id: generateId(),
                  start: finalStart,
                  end: finalEnd,
                  distance: finalDistance,
                  showDistanceLabel: true,
                  startSignId: startSign?.id,
                  endSignId: endSign?.id,
                },
              ]);
              setArrowDraft(null);
            }}
            onWorkzoneDrawingComplete={() => {
              setCurrentTool('cursor');
              setIsDrawingWorkzone(false);
            }}
            onArrowDistanceChange={handleArrowDistanceChange}
            onArrowDelete={handleArrowDelete}
            onArrowMove={handleArrowMove}
            delineatorDraft={delineatorDraft}
            delineatorLines={delineatorLines}
            onDelineatorDraftStart={handleDelineatorDraftStart}
            onDelineatorDraftAddPoint={handleDelineatorDraftAddPoint}
            onDelineatorComplete={handleDelineatorComplete}
            onDelineatorDelete={handleDelineatorDelete}
            onDelineatorUpdate={handleDelineatorUpdate}
            selectedElement={selectedElement}
            onElementSelect={handleElementSelect}
            onClearSelection={handleClearSelection}
            workzones={workzones}
            roadToolGeometry={roadToolGeometry}
            roadToolVisible={roadToolVisible}
            isProMode={plan?.ai_generation || false}
            onMapFadeChange={setMapFadeOpacity}
            paperWidth={getPaperDimensions(selectedPaperSize, selectedOrientation).pixelWidth}
            paperHeight={getPaperDimensions(selectedPaperSize, selectedOrientation).pixelHeight}
            onPaperSizeChange={(width, height, sizeName) => {
              // Parse the size name to extract paper size and orientation
              const parts = sizeName.split(' ');
              const size = parts[0] as PaperSize;
              const orientation = parts[1].toLowerCase() as Orientation;
              setSelectedPaperSize(size);
              setSelectedOrientation(orientation);
            }}
            loadedWorksiteLocation={worksiteLocation}
          />
        )}
        
        {/* Generate TGS button - temporarily disabled
        {hasWorkzonePolygon && plan?.ai_generation && (
          <Button
            onClick={() => setShowGenerateDialog(true)}
            variant="contained"
            sx={{
              position: 'absolute',
              top: 32,
              left: '50%',
              transform: 'translateX(calc(-50% + 300px))',
              zIndex: 21,
              borderRadius: 999,
              px: 2,
              py: 1,
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0px 18px 40px rgba(15, 23, 42, 0.22)',
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            Generate TGS
          </Button>
        )}
        */}

        <Generate
          workzone={workzones}
          open={showGenerateDialog}
          onclose={() => setShowGenerateDialog(false)}
          onSignsGenerated={handleSignsGenerated}
        />
      </Box>
      

      {/* Print Area Confirmed Dialog */}
      <Dialog open={showPrintAreaConfirmDialog} onClose={() => setShowPrintAreaConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Print Area Selected</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your print area has been defined. You can now:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                setShowPrintAreaConfirmDialog(false);
                handleOpenAnnotationEditor();
              }}
              sx={{
                py: 1.5,
                backgroundColor: '#6366F1',
                '&:hover': { backgroundColor: '#4F46E5' },
                textTransform: 'none',
              }}
            >
              Edit Annotations
            </Button>
            <Button
              variant="outlined"
              onClick={async () => {
                setShowPrintAreaConfirmDialog(false);
                // Capture the map canvas, then open annotation editor in auto-export mode
                // This follows the same WYSIWYG path as the annotation editor with no elements
                if (mapControls?.captureCanvas && printArea) {
                  try {
                    const canvas = await mapControls.captureCanvas(undefined, mapFadeOpacity);
                    setCapturedMapCanvas(canvas);
                  } catch (e) {
                    console.error('Failed to capture map canvas for skip export:', e);
                  }
                }
                setAutoExportAnnotations(true);
                setShowAnnotationEditor(true);
              }}
              sx={{
                py: 1.5,
                borderColor: 'rgba(0,0,0,0.23)',
                color: 'text.primary',
                textTransform: 'none',
              }}
            >
              Skip & Export Now
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowPrintAreaConfirmDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <CompositeSignBuilder
        open={showCompositeBuilder}
        onClose={() => {
          setShowCompositeBuilder(false);
          setCustomSignsReloadKey(prev => prev + 1); // Trigger reload of custom signs
        }}
        onSave={handleSaveComposite}
        signLibrary={signLibrary}
      />

      <Dialog open={showFormatDialog} onClose={() => setShowFormatDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Choose Export Format</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value as 'pdf' | 'png' | 'jpeg')}
            >
              <FormControlLabel value="pdf" control={<Radio />} label="PDF - Professional document with sign details" />
              <FormControlLabel value="png" control={<Radio />} label="PNG - High quality image with transparency" />
              <FormControlLabel value="jpeg" control={<Radio />} label="JPEG - Compressed image format" />
            </RadioGroup>
          </FormControl>

          {/* Paper Settings */}
          <PrintSettings
            paperSize={selectedPaperSize}
            orientation={selectedOrientation}
            onPaperSizeChange={setSelectedPaperSize}
            onOrientationChange={setSelectedOrientation}
          />

          {/* Resolution Selector */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="resolution-select-label">Map Quality</InputLabel>
              <Select
                labelId="resolution-select-label"
                id="resolution-select"
                value={selectedResolution}
                label="Map Quality"
                onChange={(e) => setSelectedResolution(e.target.value as '512' | '1024')}
              >
                <MenuItem value="512">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box>
                      <Typography variant="body1">Standard Quality</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Fast export using current screen view
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <MenuItem value="1024">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        High Resolution
                        <Chip 
                          label="★ PRO" 
                          size="small" 
                          color="primary" 
                          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                        />
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Fetches high-res satellite imagery (1024×1024 tiles)
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFormatDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => handleExportWithFormat(selectedFormat, selectedResolution)} 
            variant="contained"
            disabled={isLoading}
          >
            Export {selectedFormat.toUpperCase()}
          </Button>
        </DialogActions>
      </Dialog>


      <TrafficSpecialistChat
        open={showTrafficChat}
        onClose={() => setShowTrafficChat(false)}
        signPlacement={signPlacement}
        workzones={workzones}
        onRequestWorkzone={() => {
          // Trigger workzone drawing when user clicks "Set Location" in chat
          setShowTrafficChat(false); // Close chat to show map
          handleToolChange('workzone'); // Activate workzone drawing tool
        }}
        captureCanvas={async () => {
          // Capture the map canvas for vision-based compliance analysis
          if (mapControls?.captureCanvas) {
            return await mapControls.captureCanvas(undefined, mapFadeOpacity);
          }
          return null;
        }}
      />

      {/* Annotation Editor - Full-screen modal */}
      <AnnotationEditor
        open={showAnnotationEditor}
        onClose={handleCloseAnnotationEditor}
        onExport={handleAnnotationExport}
        onSaveProject={handleSaveProject}
        onSaveTemplate={handleSaveAnnotationTemplate}
        capturedCanvas={capturedMapCanvas}
        paperSize={selectedPaperSize}
        orientation={selectedOrientation}
        signs={signPlacement?.signs?.map((s: any) => ({
          code: s.sign?.code_sa || '',
          description: s.sign?.description || '',
          category: s.sign?.category || '',
        })) || []}
        delineators={delineatorLines.map(line => ({
          deviceType: line.deviceType,
          count: Math.ceil(line.path.length > 1 
            ? line.path.reduce((acc, point, i, arr) => {
                if (i === 0) return 0;
                const prev = arr[i - 1];
                const dx = point.lng - prev.lng;
                const dy = point.lat - prev.lat;
                return acc + Math.sqrt(dx * dx + dy * dy) * 111000; // rough meters
              }, 0) / (line.spacing || 3)
            : 0
          ),
        }))}
        workzones={workzones}
        autoExport={autoExportAnnotations}
      />

    </Box>
  );
}

// Loading Screen
const LoadingScreen: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: 2
    }}
  >
    <CircularProgress size={60} />
    <Typography variant="h6">Loading Traffic Designer...</Typography>
  </Box>
);

// App Content Router
function AppContent() {
  const { user, loading, authStatus } = useAuth();
  const { signOut } = useClerk();
  const [showPaywall, setShowPaywall] = useState(false);

  const handleClosePaywall = () => {
    // Log out the user when they close the subscription dialog
    signOut();
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <SignInScreen />;
  }

  // Check if authStatus and plan exist before accessing manual_mode
  if (!authStatus || !authStatus.plan || !authStatus.plan.manual_mode) {
    return <SubscriptionDialog open={true} onClose={handleClosePaywall} />;
  }

  return <AuthenticatedApp />;
}

// Main App with Clerk Provider
function App() {
  if (!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ p: 4 }}>
          <Alert severity="error">
            Missing Clerk configuration. Please set REACT_APP_CLERK_PUBLISHABLE_KEY in your .env file.
          </Alert>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ClerkProvider publishableKey={process.env.REACT_APP_CLERK_PUBLISHABLE_KEY}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </ClerkProvider>
  );
}

export default App;